/**
 * Las tres zonas de la plataforma.
 *
 * Cada una tiene su propio publico y su propia forma de entrar:
 *   gestion    -> equipo de DM, login con Google
 *   pedidos    -> empresas clientes, correo y contrasena
 *   solicitud  -> candidatos, link con token y sin cuenta
 *
 * En produccion cada zona vive en su subdominio. Hasta que existan los DNS
 * funcionan por ruta sobre la IP. Este modulo resuelve las dos formas sin que
 * el resto de la aplicacion se entere: cada zona escribe sus rutas como si
 * estuviera en la raiz, y el basename las acomoda.
 */

export const ZONAS = ['gestion', 'pedidos', 'solicitud'] as const;
export type Zona = (typeof ZONAS)[number];

export const ZONA_TITULO: Record<Zona, string> = {
  gestion: 'Gestión',
  pedidos: 'Pedidos',
  solicitud: 'Solicitud',
};

export const ZONA_DESCRIPCION: Record<Zona, string> = {
  gestion: 'Equipo de Socioambientales DM',
  pedidos: 'Empresas clientes',
  solicitud: 'Candidatos',
};

function esZona(v: string): v is Zona {
  return (ZONAS as readonly string[]).includes(v);
}

export interface ZonaResuelta {
  zona: Zona;
  /**
   * Prefijo que hay que pasarle al router. Con subdominio es "/" porque la zona
   * ya vive en la raiz; por ruta es el primer segmento tal cual vino en la URL,
   * respetando mayusculas, para que coincida con lo que el usuario tipeo.
   */
  basename: string;
  /** true cuando se resolvio por subdominio. */
  porSubdominio: boolean;
}

/** Devuelve null cuando no se puede determinar la zona (raiz sin subdominio). */
export function resolverZona(
  host = window.location.hostname,
  pathname = window.location.pathname,
): ZonaResuelta | null {
  const sub = host.split('.')[0].toLowerCase();
  if (esZona(sub)) {
    return { zona: sub, basename: '/', porSubdominio: true };
  }

  const primerSegmento = pathname.split('/').filter(Boolean)[0];
  if (primerSegmento && esZona(primerSegmento.toLowerCase())) {
    return {
      zona: primerSegmento.toLowerCase() as Zona,
      basename: `/${primerSegmento}`,
      porSubdominio: false,
    };
  }

  return null;
}

/**
 * La direccion de otra zona, servida como se este sirviendo esta.
 *
 * En produccion cada zona es un subdominio; en desarrollo y sobre la IP son
 * rutas del mismo host. Quien quiera abrir el portal desde gestion no deberia
 * tener que saber cual de las dos es.
 */
export function urlDeZona(zona: Zona, ruta = '/'): string {
  const { origin, hostname } = window.location;
  const actual = resolverZona();
  const camino = ruta.startsWith('/') ? ruta : `/${ruta}`;

  if (actual?.porSubdominio) {
    const resto = hostname.split('.').slice(1).join('.');
    return `${window.location.protocol}//${zona}.${resto}${camino}`;
  }
  return `${origin}/${zona}${camino}`;
}
